import { Component, EventEmitter, OnDestroy, OnInit, Output, Input, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { Customer } from '@pos-app/data';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { CoreUiPartialState } from '../../+state/core-ui/core-ui.reducer';
import { getExternalCustomers, getSearchingExternalCustomer, getHasMoreRecords } from '../../+state/core-ui/core-ui.selectors';
import { clearExternalSearchData, searchExternalCustomers } from '../../+state/core-ui/core-ui.actions';
import { ApiService } from '../../services';

@Component({
    selector: 'pos-app-lib-customer-external-search',
    templateUrl: './customer-external-search.component.html',
    styleUrls: ['./customer-external-search.component.scss'],
    standalone: false
})
export class CustomersExternalSearchComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('searchBox') searchBox: ElementRef;

  @Input() forOrderSearch: boolean;
  @Input() forOrderHeader: boolean;
  @Input() selectedCustomerName: string;
  @Input() disabled: boolean;
  @Input() removeCustomerAfterLoad: boolean;
  @Input() placeHolder: string;
  @Input() showSearchIcon: boolean;

  @Output() selectNewCustomer = new EventEmitter<Customer>();

  unSubscribe$ = new Subject<void>();
  loading$: Observable<boolean>;
  externalCustomer$: Observable<Customer[]>;
  hasMoreRecords$: Observable<boolean>;
  hasMoreRecords: boolean;
  externalCustomerSelected: Customer = null;
  listOfCustomers: Customer[];
  defaultPlaceHolder: string = 'External Customer';

  indexFocus = 0;
  show = false;
  externalCustomerSearchForm: UntypedFormGroup;
  selectType: boolean;

  constructor(private coreUIStore: Store<CoreUiPartialState>, private fb: UntypedFormBuilder, private apiService: ApiService) {}

  ngOnInit() {
    this.placeHolder = this.placeHolder ? this.placeHolder : this.defaultPlaceHolder;
    this.coreUIStore.dispatch(clearExternalSearchData());
    this.externalCustomerSearchForm = this.fb.group({
      externalCustomerSearch: [{ value: '', disabled: this.disabled }],
      customerSearchType: [''],
    });
    this.externalCustomer$ = this.coreUIStore.pipe(select(getExternalCustomers));
    this.externalCustomer$.pipe(takeUntil(this.unSubscribe$)).subscribe((x) => {
      this.listOfCustomers = x;
      const matchCustomerNumber = this.listOfCustomers.find(
        (customer) => customer.customerNumber === this.externalCustomerSearchForm.get('externalCustomerSearch').value
      );
      if (matchCustomerNumber) {
        this.selectCustomer(matchCustomerNumber);
      }
    });

    this.loading$ = this.coreUIStore.select(getSearchingExternalCustomer);

    this.hasMoreRecords$ = this.coreUIStore.select(getHasMoreRecords);

    this.hasMoreRecords$.pipe(takeUntil(this.unSubscribe$)).subscribe((hasMoreRecord) => {
      this.hasMoreRecords = hasMoreRecord;
    });
    this.onChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedCustomerName && this.externalCustomerSearchForm) {
      this.externalCustomerSearchForm.patchValue(
        { externalCustomerSearch: this.removeCustomerAfterLoad ? '' : this.selectedCustomerName },
        { emitEvent: false }
      );
    }

    if (changes.disabled && this.externalCustomerSearchForm) {
      if (this.disabled) {
        this.externalCustomerSearchForm.get('externalCustomerSearch').disable({ emitEvent: false });
      } else {
        this.externalCustomerSearchForm.get('externalCustomerSearch').enable({ emitEvent: false });
        this.searchBox.nativeElement.focus();
      }
    }
  }

  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  onKeyboard(e) {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      let newIdx = this.indexFocus + (e.key === 'ArrowDown' ? 1 : -1);
      newIdx = newIdx < 0 ? 0 : newIdx;
      this.indexFocus = newIdx % this.listOfCustomers.length;
    } else if (e.key === 'Enter') {
      this.selectCustomer(this.listOfCustomers[this.indexFocus]);
    } else {
      this.show = true;
    }
  }
  lostFocus(e) {
    setTimeout(() => {
      this.show = false;
      if (this.selectType) {
        this.searchBox.nativeElement.focus();
        this.selectType = false;
      }
    }, 300);
  }

  selectCustomer(customer: any) {
    this.show = false;
    this.externalCustomerSelected = customer;
    this.apiService
      .fetchCustomerDetails({
        customerNumber: customer?.parentCustomerNumber,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((data) => {
        if (data?.ErrorFlag === '0') {
          const customer = data.SearchResults[0];
          this.externalCustomerSelected = customer;
          this.externalCustomerSearchForm.patchValue({ externalCustomerSearch: '' }, { emitEvent: false });
          this.selectNewCustomer.emit(this.externalCustomerSelected);
          this.coreUIStore.dispatch(clearExternalSearchData());
        }
      });
  }

  onChanges() {
    this.externalCustomerSearchForm
      .get('externalCustomerSearch')
      .valueChanges.pipe(
        tap((value) => {
          this.externalCustomerSelected = null;
          this.listOfCustomers = [];
          this.hasMoreRecords = false;
          this.selectNewCustomer.emit(null);
          this.externalCustomerSearchForm.patchValue({ customerSearchType: '' }, { emitEvent: false });
        }),
        debounceTime(500),
        distinctUntilChanged(),
        filter((v) => (v || '').trim().length >= 3),
        takeUntil(this.unSubscribe$)
      )
      .subscribe((value: string) => {
        this.coreUIStore.dispatch(clearExternalSearchData());
        this.coreUIStore.dispatch(
          searchExternalCustomers({
            searchString: value.replace('-', ''),
          })
        );
      });

    this.externalCustomerSearchForm
      .get('customerSearchType')
      .valueChanges.pipe(takeUntil(this.unSubscribe$))
      .subscribe((value) => {
        this.coreUIStore.dispatch(
          searchExternalCustomers({
            searchString: value.replace('-', ''),
          })
        );
        this.selectType = true;
      });
  }

  getLocation(customer: Customer) {
    return (customer.suburb ? `${customer.suburb}, ` : '') + (customer.state ? `${customer.state}, ` : '') + customer.countryName;
  }
  isDisabled() {
    return this.disabled;
  }
  isFocus(e) {
    this.show = true;
    e.target.select();
  }
}

